import React, { Component } from 'react';
import { Form, Field } from 'react-final-form';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { NAMESPACES } from '../../i18n';
import PropTypesImmutable from 'react-immutable-proptypes';
import { ClassificatorField, CheckboxField, DateRangeField } from '../../atoms';
import {
  FormActions,
  TableActions,
  AutoCompleteProductionUnitField,
} from '../../components';
import { connect } from 'react-redux';
import { Collapse, Row, Col, Button } from 'antd';
import { FORM_ACCOUNT_REPORT_FILTER } from '../../constants/formKeys';
import * as actions from '../../actions/actionCreators/accountActionCreators';
import { getUserLegalEntity } from '../../reducers/userReducer';
import { formAccountOverviewFilter } from '../../constants/forms/formAccountOverviewFilter';
import { FORMAT_DEFAULT } from '../../utils';
import { links } from '../../utils';
import { downloadFileByPostURL } from '../../utils/files';
import { legalEntityFields } from '../../constants/legalEntity';
import { gas } from '../../constants/classificators';
import _ from 'lodash';
import dayjs from '../../utils/dayjs';
import RffFields from '../../atoms/rff/RffFields';
import FormStateToRedux from '../FormStateToRedux';
import {
  updateFormState,
  formValueSelector,
} from '../../reducers/reactFinalFormReducer';

const { Panel } = Collapse;

const getInitialValues = (accountIds, state) => {
  const legalEntity = getUserLegalEntity(state);
  let gasValue = null;
  if (legalEntity) {
    const isHydrogenManufacturer = legalEntity.get(
      legalEntityFields.HYDROGEN_MANUFACTURER
    );
    const isBiomethaneManufacturer = legalEntity.get(
      legalEntityFields.MANUFACTURER
    );
    const isTrader = legalEntity
      .get('accounts')
      .some(
        (account) =>
          accountIds?.includes(account.get('id') + '') &&
          account.get('accountType') === 'TRADER'
      );
    if (!isTrader) {
      if (isHydrogenManufacturer && !isBiomethaneManufacturer) {
        gasValue = gas.HYDROGEN;
      } else if (!isHydrogenManufacturer && isBiomethaneManufacturer) {
        gasValue = gas.BIOMETHANE;
      }
    }
  }
  let ids = null;
  if (accountIds) {
    ids = accountIds.filter((id) => id !== null);
  }
  return {
    [formAccountOverviewFilter.BALANCE_START_DATE]: dayjs()
      .startOf('day')
      .format(FORMAT_DEFAULT),
    [formAccountOverviewFilter.BALANCE_END_DATE]: dayjs()
      .endOf('day')
      .format(FORMAT_DEFAULT),
    [formAccountOverviewFilter.GAS_TYPE]: gasValue,
    accountIds: ids,
  };
};

export class AccountReportFilter extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    legalEntity: PropTypesImmutable.map,
    fetchAccountReport: PropTypes.func.isRequired,
    accountIds: PropTypes.arrayOf(PropTypes.string),
    updateFormState: PropTypes.func.isRequired,
    filterValues: PropTypes.object,
    initialValues: PropTypes.object,
  };

  componentDidMount = () => {
    const {
      fetchAccountReport,
      initialValues,
      updateFormState,
      filterValues,
      accountIds,
    } = this.props;

    // BMR-1184: preserve the previously-entered filter across leave/return
    // navigation (replaces redux-form `destroyOnUnmount: false`). The persisted
    // slice lives in state.rff via FormStateToRedux and is read back here.
    const persisted = filterValues ? filterValues.toJS() : {};
    const persistedAccountIds = filterValues
      ? filterValues.get('accountIds')
      : null;

    if (!_.isEqual(persistedAccountIds, accountIds || null)) {
      // account selection changed since the last visit → start fresh
      this._initialValues = initialValues;
    } else if (persisted[formAccountOverviewFilter.BALANCE_START_DATE]) {
      // returning with a previously-entered filter → restore fields and data
      this._initialValues = { ...persisted, accountIds: accountIds || null };
    } else {
      this._initialValues = initialValues;
    }

    updateFormState(FORM_ACCOUNT_REPORT_FILTER, {
      values: this._initialValues,
    });
    fetchAccountReport(this._initialValues);
  };

  componentDidUpdate = (prevProps) => {
    const { fetchAccountReport, accountIds, updateFormState } = this.props;

    if (!_.isEqual(accountIds, prevProps.accountIds)) {
      this._initialValues = this.props.initialValues;
      if (this._form) {
        this._form.restart(this._initialValues);
      }
      updateFormState(FORM_ACCOUNT_REPORT_FILTER, {
        values: this._initialValues,
      });
      fetchAccountReport(this._initialValues);
    }
  };

  onClear = (form) => {
    const { updateFormState } = this.props;
    form.restart(this._initialValues);
    updateFormState(FORM_ACCOUNT_REPORT_FILTER, {
      values: this._initialValues,
    });
  };

  onGasTypeChange = (form) => {
    form.change(formAccountOverviewFilter.GROUP_BY_TECHNOLOGY, false);
    form.change(formAccountOverviewFilter.GROUP_BY_HYDROGEN_TECHNOLOGY, false);
    form.change(formAccountOverviewFilter.GROUP_BY_FUEL_TYPE, false);
    form.change(formAccountOverviewFilter.GROUP_BY_HYDROGEN_FUEL_TYPE, false);
    form.change(formAccountOverviewFilter.FUEL_TYPE, null);
    form.change(formAccountOverviewFilter.HYDROGEN_TECHNOLOGY_TYPE, null);
    form.change(formAccountOverviewFilter.HYDROGEN_FUEL_TYPE, null);
    form.change(formAccountOverviewFilter.CLEANING_TECHNOLOGY_TYPE, null);
  };

  onSubmit = (values) => {
    // BMR-1184: carry the selected accounts into the request and normalize an
    // empty gas selection to null (avoids querying with gasType === '').
    const payload = { ...values, accountIds: this.props.accountIds || null };
    if (payload[formAccountOverviewFilter.GAS_TYPE] === '') {
      payload[formAccountOverviewFilter.GAS_TYPE] = null;
    }
    this.props.fetchAccountReport(payload);
  };

  handleExportTransactions = () => {
    const { filterValues } = this.props;

    downloadFileByPostURL(
      links.periodicReportExportCsv,
      filterValues ? filterValues.toJS() : {},
      'periodic-report.csv'
    );
  };

  render() {
    const { t, accountIds, legalEntity } = this.props;
    let isTrader = null;
    if (legalEntity) {
      isTrader = legalEntity
        .get('accounts')
        .some(
          (account) =>
            accountIds?.includes(account.get('id') + '') &&
            account.get('accountType') === 'TRADER'
        );
    }

    return (
      <React.Fragment>
        <Form
          onSubmit={this.onSubmit}
          initialValues={this._initialValues}
          render={({ handleSubmit, form, values }) => {
            this._form = form;
            const gasFieldValue = values[formAccountOverviewFilter.GAS_TYPE];
            return (
              <Collapse defaultActiveKey={['filters']}>
                <Panel key="filters" header={t('filter.title')}>
                  <form onSubmit={handleSubmit}>
                    <FormStateToRedux form={FORM_ACCOUNT_REPORT_FILTER} />
                    <Row gutter={32}>
                      <Col span={16}>
                        <Row gutter={32}>
                          <Col span={24}>
                            <RffFields
                              className="date-field"
                              label={t('filter.balanceDateRange.title')}
                              names={[
                                formAccountOverviewFilter.BALANCE_START_DATE,
                                formAccountOverviewFilter.BALANCE_END_DATE,
                              ]}
                              placeholder={[
                                t(
                                  'filter.balanceDateRange.dateFromPlaceholder'
                                ),
                                t('filter.balanceDateRange.dateToPlaceholder'),
                              ]}
                              component={DateRangeField}
                              hasFeedback={false}
                              required
                            />
                          </Col>
                        </Row>
                        <Row gutter={32}>
                          <Col span={12}>
                            <Field
                              label={t('overview.filter.gas')}
                              name={formAccountOverviewFilter.GAS_TYPE}
                              component={ClassificatorField}
                              classificatorType={ClassificatorField.types.gas}
                              hasFeedback={false}
                              onChange={() => this.onGasTypeChange(form)}
                            />
                          </Col>
                          {!isTrader && (
                            <Col span={12}>
                              <Field
                                label={t('overview.filter.productionUnit')}
                                name={
                                  formAccountOverviewFilter.PRODUCTION_UNIT_ID
                                }
                                component={AutoCompleteProductionUnitField}
                                hasFeedback={false}
                              />
                            </Col>
                          )}
                        </Row>
                        {gasFieldValue === gas.BIOMETHANE && (
                          <Row gutter={32}>
                            <Col span={12}>
                              <Field
                                label={t('overview.filter.fuelType')}
                                name={formAccountOverviewFilter.FUEL_TYPE}
                                component={ClassificatorField}
                                classificatorType={
                                  ClassificatorField.types.fuelType
                                }
                                hasFeedback={false}
                              />
                            </Col>
                            <Col span={12}>
                              <Field
                                label={t(
                                  'overview.filter.cleaningTechnologyType'
                                )}
                                name={
                                  formAccountOverviewFilter.CLEANING_TECHNOLOGY_TYPE
                                }
                                component={ClassificatorField}
                                classificatorType={
                                  ClassificatorField.types
                                    .cleaningTechnologyType
                                }
                                hasFeedback={false}
                              />
                            </Col>
                          </Row>
                        )}
                        {gasFieldValue === gas.HYDROGEN && (
                          <Row gutter={32}>
                            <Col span={12}>
                              <Field
                                label={t('overview.filter.hydrogenType')}
                                name={
                                  formAccountOverviewFilter.HYDROGEN_FUEL_TYPE
                                }
                                component={ClassificatorField}
                                classificatorType={
                                  ClassificatorField.types.hydrogenType
                                }
                                hasFeedback={false}
                              />
                            </Col>
                            <Col span={12}>
                              <Field
                                label={t(
                                  'overview.filter.hydrogenTechnologyType'
                                )}
                                name={
                                  formAccountOverviewFilter.HYDROGEN_TECHNOLOGY_TYPE
                                }
                                component={ClassificatorField}
                                classificatorType={
                                  ClassificatorField.types
                                    .hydrogenTechnologyType
                                }
                                hasFeedback={false}
                              />
                            </Col>
                          </Row>
                        )}
                      </Col>
                      <Col span={8}>
                        <span className="page-AccountOverviewPage__groupByLabel">
                          {t('overview.filter.groupBy')}
                        </span>
                        <div className="page-AccountOverviewPage__groupBy">
                          {!legalEntity && (
                            <Field
                              name={
                                formAccountOverviewFilter.GROUP_BY_PRODUCTION_UNIT
                              }
                              type="checkbox"
                              component={CheckboxField}
                              hasFeedback={false}
                              checkboxText={t(
                                'overview.filter.groupByProductionUnit'
                              )}
                            />
                          )}
                          {gasFieldValue === gas.BIOMETHANE && (
                            <>
                              <Field
                                name={
                                  formAccountOverviewFilter.GROUP_BY_TECHNOLOGY
                                }
                                type="checkbox"
                                component={CheckboxField}
                                hasFeedback={false}
                                checkboxText={t(
                                  'overview.filter.groupByTechnology'
                                )}
                              />
                              <Field
                                name={
                                  formAccountOverviewFilter.GROUP_BY_FUEL_TYPE
                                }
                                type="checkbox"
                                component={CheckboxField}
                                hasFeedback={false}
                                checkboxText={t(
                                  'overview.filter.groupByFuelType'
                                )}
                              />
                            </>
                          )}
                          {gasFieldValue === gas.HYDROGEN && (
                            <>
                              <Field
                                name={
                                  formAccountOverviewFilter.GROUP_BY_HYDROGEN_TECHNOLOGY
                                }
                                type="checkbox"
                                component={CheckboxField}
                                hasFeedback={false}
                                checkboxText={t(
                                  'overview.filter.groupByHydrogenTechnology'
                                )}
                              />
                              <Field
                                name={
                                  formAccountOverviewFilter.GROUP_BY_HYDROGEN_FUEL_TYPE
                                }
                                type="checkbox"
                                component={CheckboxField}
                                hasFeedback={false}
                                checkboxText={t(
                                  'overview.filter.groupByHydrogeFuelType'
                                )}
                              />
                            </>
                          )}
                        </div>
                      </Col>
                    </Row>
                    <FormActions>
                      <Button onClick={() => this.onClear(form)}>
                        {t('filter.clearBtn')}
                      </Button>
                      <Button type="primary" htmlType="submit">
                        {t('filter.submitBtn')}
                      </Button>
                    </FormActions>
                  </form>
                </Panel>
              </Collapse>
            );
          }}
        />
        <TableActions>
          <Button onClick={this.handleExportTransactions}>
            {t('exportCsvBtn')}
          </Button>
        </TableActions>
      </React.Fragment>
    );
  }
}

const getValues = formValueSelector(FORM_ACCOUNT_REPORT_FILTER);

const mapStateToProps = (state, ownProps) => ({
  legalEntity: getUserLegalEntity(state),
  initialValues: getInitialValues(ownProps.accountIds, state),
  filterValues: getValues(state),
});

const mapDispatchToProps = {
  fetchAccountReport: actions.fetchAccountReport,
  updateFormState,
};

const AccountReportFilterHoc = connect(
  mapStateToProps,
  mapDispatchToProps
)(AccountReportFilter);

export default AccountReportFilterHoc;

export const AccountReportFilterTranslated = withTranslation(
  NAMESPACES.account
)(AccountReportFilterHoc);
